<script setup lang="ts">
import { computed, toRef } from "vue";
import draggable from "vuedraggable";
import { Eye, EyeOff, GripVertical, Info } from "lucide-vue-next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/episto-ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip } from "@/components/ui/tooltip";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import type { RadarOptions } from "@/composables/useRadarChartData";

interface AnswerRow {
  code: string;
  label: string;
  excluded: boolean;
}

const props = defineProps({
  card: { type: Object, required: true },
  cardMeta: { type: Object, default: null },
  displayLocale: { type: String, default: null },
});

const cardRef = toRef(props, "card");
const { localOptions, updateLocalOption } = useCardLocalOptions(cardRef);

const radar = computed<RadarOptions>(() => localOptions.value.radar || {});

function patch(changes: Partial<RadarOptions>) {
  updateLocalOption("radar", { ...radar.value, ...changes });
}

const SCALE_OPTIONS = [
  { value: "fit", label: "Zoom to data" },
  { value: "full", label: "Full scale" },
  { value: "manual", label: "Manual" },
];

const scaleMode = computed(() => radar.value.scale_mode || "fit");

function numberOrNull(value: string | number): number | null {
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
}

const isLikert = computed(() => !["rating", "nps"].includes(props.cardMeta?.specs?.matrix_type));

const specAnswers = computed<{ code: string; label: string }[]>(() =>
  (props.cardMeta?.specs?.answers || []).map((answer: { title?: unknown }, idx: number) => {
    const code = String(idx + 1);
    const label = removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(answer.title, props.displayLocale) || "");
    return { code, label: label || code };
  }),
);

const answerRows = computed<AnswerRow[]>(() => {
  const byCode = new Map(specAnswers.value.map((answer) => [answer.code, answer.label]));
  const saved = (radar.value.scale_codes || []).filter((code) => byCode.has(code));
  const missing = specAnswers.value.map((answer) => answer.code).filter((code) => !saved.includes(code));
  const excluded = new Set(radar.value.excluded_codes || []);
  return [...saved, ...missing].map((code) => ({
    code,
    label: byCode.get(code) || code,
    excluded: excluded.has(code),
  }));
});

function onReorder(rows: AnswerRow[]) {
  patch({ scale_codes: rows.map((row) => row.code) });
}

function toggleExcluded(code: string) {
  const excluded = new Set(radar.value.excluded_codes || []);
  if (excluded.has(code)) excluded.delete(code);
  else excluded.add(code);
  patch({ excluded_codes: [...excluded] });
}
</script>

<template>
  <div class="space-y-3">
    <div class="space-y-2">
      <div class="flex items-center gap-1.5">
        <Label class="font-medium">Scale</Label>
        <Tooltip
          content="Zoom to data spreads the chart over the observed range so small differences stay visible. Full scale shows the whole answer scale."
          :delay-duration="300">
          <Info class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Tooltip>
      </div>
      <Select
        :model-value="scaleMode"
        :options="SCALE_OPTIONS"
        @update:model-value="patch({ scale_mode: $event })" />
      <div
        v-if="scaleMode === 'manual'"
        class="grid grid-cols-2 gap-2">
        <div class="space-y-1">
          <Label class="text-xs text-muted-foreground">Min</Label>
          <Input
            type="number"
            step="0.5"
            :model-value="radar.scale_min ?? ''"
            @update:model-value="patch({ scale_min: numberOrNull($event) })" />
        </div>
        <div class="space-y-1">
          <Label class="text-xs text-muted-foreground">Max</Label>
          <Input
            type="number"
            step="0.5"
            :model-value="radar.scale_max ?? ''"
            @update:model-value="patch({ scale_max: numberOrNull($event) })" />
        </div>
      </div>
    </div>

    <template v-if="isLikert && answerRows.length">
      <div class="space-y-2">
        <div class="flex items-center gap-1.5">
          <Label class="font-medium">Answer scale</Label>
          <Tooltip
            content="Drag to set the value order, top is the lowest value. Hide the answers that are not part of the scale, like “Don't know”."
            :delay-duration="300">
            <Info class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Tooltip>
        </div>
        <draggable
          :model-value="answerRows"
          item-key="code"
          handle=".scale-drag"
          class="space-y-1"
          @update:model-value="onReorder">
          <template #item="{ element }">
            <div
              class="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
              :class="{ 'opacity-50': element.excluded }">
              <GripVertical class="scale-drag h-4 w-4 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing" />
              <span
                class="min-w-0 flex-1 truncate text-xs"
                :class="{ 'line-through': element.excluded }"
                :title="element.label">
                {{ element.label }}
              </span>
              <button
                type="button"
                class="shrink-0 text-muted-foreground hover:text-foreground"
                :aria-label="element.excluded ? 'Include in the scale' : 'Hide from the scale'"
                @click="toggleExcluded(element.code)">
                <EyeOff
                  v-if="element.excluded"
                  class="h-3.5 w-3.5" />
                <Eye
                  v-else
                  class="h-3.5 w-3.5" />
              </button>
            </div>
          </template>
        </draggable>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <Label :class="radar.reversed ? 'text-foreground font-medium' : 'text-muted-foreground'">Reverse order</Label>
          <Tooltip
            content="The top of the list counts as the highest value instead."
            :delay-duration="300">
            <Info class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Tooltip>
        </div>
        <Switch
          :model-value="radar.reversed === true"
          @update:model-value="patch({ reversed: $event })" />
      </div>
    </template>
  </div>
</template>
